import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/context';
import { CACHE } from '../api/cache';
import { Banner } from '../design';

// The operator's active announcements for this tenant/user, rendered as dismissible banners at the
// top of the shell. Reads the computed feed (GET <prefix>/announcements/active); on the admin engine
// (prefix /v1/admin) that path 404s → empty → nothing renders, so this is inert outside the tenant
// app. Never blocks the shell: any error resolves to "no banners".

const MAX_VISIBLE = 3;

interface Announcement {
  id: string;
  title: string;
  body: string | null;
  tone: 'info' | 'warn' | 'pos' | 'error';
  link: string | null;
  dismissible: boolean;
}

export function AnnouncementBanner() {
  const api = useApi();
  const qc = useQueryClient();
  const key = ['announcements-active', api.prefix];
  const { data } = useQuery<Announcement[]>({
    queryKey: key,
    ...CACHE.poll,
    refetchInterval: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const res = await api.request<{ data: Announcement[] }>(`${api.prefix}/announcements/active`);
        return res.data ?? [];
      } catch {
        return [];
      }
    },
  });

  const dismiss = async (id: string) => {
    try {
      await api.request(`${api.prefix}/announcements/${encodeURIComponent(id)}/dismiss`, { method: 'POST', body: {} });
    } catch {
      /* ignore — best-effort */
    }
    void qc.invalidateQueries({ queryKey: key });
  };

  if (!data || data.length === 0) return null;
  // Two announcements that say the same thing are one banner; more than three at once is a wall.
  const seen = new Set<string>();
  const unique = data.filter((a) => {
    const k = `${a.title}\u0000${a.body ?? ''}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const shown = unique.slice(0, MAX_VISIBLE);
  const hidden = unique.length - shown.length;
  return (
    <div className="hf-announcements">
      {shown.map((a) => (
        <Banner
          key={a.id}
          tone={a.tone}
          action={
            a.dismissible ? (
              <button type="button" className="hf-btn-ghost hf-btn-sm" onClick={() => void dismiss(a.id)} aria-label={`Dismiss ${a.title}`}>
                Dismiss
              </button>
            ) : a.link ? (
              <a href={a.link} className="hf-link">
                Learn more
              </a>
            ) : undefined
          }
        >
          <strong>{a.title}</strong>
          {a.body ? <> — {a.body}</> : null}
        </Banner>
      ))}
      {hidden > 0 && (
        <Banner tone="info">
          {hidden} more {hidden === 1 ? 'announcement' : 'announcements'} — dismiss one above to see the next.
        </Banner>
      )}
    </div>
  );
}
